import { useCookies } from 'composables/cookies';
import { useFischer } from 'composables/fischerPlugin';
import { useSsrCookies } from 'composables/ssrCookies';
import { computed } from 'vue';
import type { VueCookies } from 'vue-cookies';

export interface HandlingFeeLimitCookie {
  id: string;
  value: number;
  isocode: string;
  noBanner?: boolean;
}

export const cookieName = 'handlingfeelimit2';

export const clearHandlingFeeLimitCookieFunction = (cookies: VueCookies) => {
  if (IS_SSR) {
    return;
  }
  cookies.remove(cookieName);
};

export const useHandlingFeeCookie = () => {
  const fischer = useFischer();
  const { cookies } = useCookies();
  const { getCookie } = useSsrCookies();

  const formatHandlingFeeLimit = (handlingFeeLimit: number, isocode: string) => {
    try {
      const formatter = new Intl.NumberFormat(fischer.siteLanguage(), {
        style: 'currency',
        currency: isocode,
      });
      return formatter.format(handlingFeeLimit);
    } catch (error) {
      console.error('Could not format handling fee limit', error);
      return `${handlingFeeLimit} ${isocode}`;
    }
  };

  const handlingFeeLimitCookieId = computed<string | null>(() => {
    const customerId = fischer.user.customerId();
    if (!customerId) {
      return null;
    }

    return customerId;
  });

  const clearHandlingFeeLimitCookie = () => clearHandlingFeeLimitCookieFunction(cookies);

  const getHandlingFeeLimitCookie = () => {
    if (!fischer.user.accessToken()) {
      return null;
    }
    const cookie = getCookie(cookieName);
    if (!cookie) {
      return null;
    }

    if (cookie.id !== handlingFeeLimitCookieId.value) {
      clearHandlingFeeLimitCookieFunction(cookies);
      return null;
    }
    return cookie as HandlingFeeLimitCookie;
  };

  const getHandlingFeeLimitFormatted = () => {
    const cookie = getHandlingFeeLimitCookie();
    if (!cookie || cookie.noBanner || cookie.value === -1) {
      return null;
    }
    return formatHandlingFeeLimit(cookie.value, cookie.isocode);
  };

  const setHandlingFeeLimitCookie = (
    handlingFeeLimit: number,
    isocode: string,
    noBanner: boolean,
  ) => {
    if (!handlingFeeLimitCookieId.value) {
      console.error('missing handling fee cookie id');
      return;
    }
    if (!IS_SSR) {
      const cookieValue: HandlingFeeLimitCookie = {
        id: handlingFeeLimitCookieId.value,
        value: handlingFeeLimit,
        isocode,
      };
      if (noBanner) {
        cookieValue.noBanner = true;
      }
      cookies.set(cookieName, cookieValue, 0, undefined, undefined, undefined, 'Strict');
    }
  };

  const disableHandlingFeeLimitBanner = () => {
    const oldData = getCookie(cookieName);
    if (oldData.id === handlingFeeLimitCookieId.value) {
      setHandlingFeeLimitCookie(oldData.value, oldData.isocode, true);
    } else {
      setHandlingFeeLimitCookie(-1, '', true);
    }
  };

  const getHandlingFeeDifference = (amount: number) => {
    const limitCookie = getHandlingFeeLimitCookie();
    const value = limitCookie?.value;
    if (!value || value === -1) {
      return null;
    }
    const diff = value - amount;
    if (!(diff > 0)) {
      return null;
    }
    return formatHandlingFeeLimit(diff, limitCookie.isocode);
  };

  return {
    clearHandlingFeeLimitCookie,
    disableHandlingFeeLimitBanner,
    getHandlingFeeDifference,
    getHandlingFeeLimitCookie,
    getHandlingFeeLimitFormatted,
    setHandlingFeeLimitCookie,
    handlingFeeLimitCookieId,
  };
};
